import { useState } from 'react';
import { usePreferences } from '../../context/usePreferences';
import { ALL_AUTHORS, authorImage, buildAuthorsQuery } from '../../lib/cmsAuthors';
import { FeedContent } from './FeedView';
import './FollowingView.css';

// The Following tab: a date-ordered feed from the columnists the user
// follows, with the management UX living right here in the tab. Follows
// apply immediately — the social convention — rather than through the
// sheet's draft-and-commit flow; the sheet only decides whether this tab
// exists at all.
//
// With nobody followed the picker *is* the tab: the empty state introduces
// the feature by showing exactly the control that fills it.

// A columnist's headshot, falling back to their initial. Decorative in both
// the rail and the picker rows — the name is always alongside or in the
// control's accessible label — hence the empty alt.
function AuthorPhoto({ author }) {
  const image = authorImage(author);

  if (!image) return author.charAt(0);

  return (
    <img
      className="following-view__photo"
      src={image}
      alt=""
      loading="lazy"
    />
  );
}

// Faces shown in the rested cluster before the rest fold into a +N circle.
const FACE_LIMIT = 4;

// The rested rail: overlapped faces as a single button that fans them out.
// When a filter is live its columnist leads the stack, ringed and sitting
// on top of the overlap, with an ✕ alongside as the one-tap way back to
// everyone. (A sibling, not a child: buttons cannot nest.)
function FacepileCluster({ authors, activeFilter, onExpand, onClearFilter }) {
  const ordered = activeFilter
    ? [activeFilter, ...authors.filter((a) => a !== activeFilter)]
    : authors;

  return (
    <div className="following-view__cluster">
      {activeFilter && (
        <button
          type="button"
          className="following-view__face following-view__face--clear"
          onClick={onClearFilter}
          aria-label={`Stop showing only ${activeFilter}`}
        >
          <span aria-hidden="true">✕</span>
        </button>
      )}
      <button
        type="button"
        className="following-view__cluster-button"
        aria-expanded={false}
        aria-label={
          activeFilter
            ? `Your columnists, showing only ${activeFilter}. Expand to change who is shown`
            : 'Your columnists. Expand to show just one'
        }
        onClick={onExpand}
      >
        <span className="following-view__cluster-faces" aria-hidden="true">
          {ordered.slice(0, FACE_LIMIT).map((author) => (
            <span
              key={author}
              className={
                author === activeFilter
                  ? 'following-view__face following-view__face--active'
                  : 'following-view__face'
              }
            >
              <AuthorPhoto author={author} />
            </span>
          ))}
          {authors.length > FACE_LIMIT && (
            <span className="following-view__face following-view__face--count">
              +{authors.length - FACE_LIMIT}
            </span>
          )}
        </span>
      </button>
    </div>
  );
}

export function FollowingView({ id, title }) {
  const { authors, setAuthors } = usePreferences();
  // Arriving with nobody followed opens the picker, and the first follow
  // must not yank it away mid-choice: the manager stays until Done is
  // pressed, however it was entered.
  const [isManaging, setIsManaging] = useState(authors.length === 0);
  // Tap a face on the fanned-out rail to hear one columnist alone; tap
  // again for everyone. Transient by design — filters are a glance, not a
  // setting.
  const [filter, setFilter] = useState(null);
  // The rail rests as an overlapped cluster; tapping fans it out into
  // individually tappable faces.
  const [isExpanded, setIsExpanded] = useState(false);

  const showManager = isManaging || authors.length === 0;
  // An unfollow in the manager can orphan the filter; fall back to everyone.
  const activeFilter = filter && authors.includes(filter) ? filter : null;

  const chooseFilter = (author) => {
    setFilter(author === activeFilter ? null : author);
    setIsExpanded(false);
  };

  const toggleAuthor = (author) =>
    setAuthors(
      authors.includes(author)
        ? authors.filter((a) => a !== author)
        : [...authors, author],
    );

  return (
    <section
      id={id}
      className="feed-view following-view"
      role="tabpanel"
      aria-label={title}
    >
      {showManager ? (
        <div className="following-view__manager">
          <header className="following-view__manager-header">
            <h2 className="following-view__manager-title">Your columnists</h2>
            {authors.length > 0 && (
              <button
                type="button"
                className="following-view__done"
                onClick={() => setIsManaging(false)}
              >
                Done
              </button>
            )}
          </header>

          {authors.length === 0 && (
            <p className="following-view__lead">
              Pick the columnists you want to hear from — their latest
              pieces gather here, newest first.
            </p>
          )}

          <ul className="following-view__list">
            {ALL_AUTHORS.map((author) => {
              const isFollowed = authors.includes(author);
              return (
                <li key={author} className="following-view__columnist">
                  <span className="following-view__avatar" aria-hidden="true">
                    <AuthorPhoto author={author} />
                  </span>
                  <span className="following-view__name">{author}</span>
                  <button
                    type="button"
                    className="following-view__follow"
                    aria-pressed={isFollowed}
                    onClick={() => toggleAuthor(author)}
                  >
                    {isFollowed ? 'Following' : 'Follow'}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <>
          {/* Rested, the rail is an overlapped cluster on the right — one
              button that fans the faces out. Fanned, every columnist is a
              tappable filter, › collapses, and the dashed + opens the
              manager. data-pager-scroll hands horizontal drags to the
              browser instead of the pager (see useTabPager). */}
          {isExpanded ? (
            <div
              className={
                activeFilter
                  ? 'following-view__rail following-view__rail--filtered'
                  : 'following-view__rail'
              }
              data-pager-scroll
            >
              <ul className="following-view__rail-list">
                <li className="following-view__rail-item">
                  <button
                    type="button"
                    className="following-view__face following-view__face--close"
                    onClick={() => setIsExpanded(false)}
                    aria-label="Collapse the columnist filters"
                  >
                    <span aria-hidden="true">›</span>
                  </button>
                </li>
                {authors.map((author, index) => (
                  <li
                    key={author}
                    className="following-view__rail-item"
                    style={{ animationDelay: `${index * 25}ms` }}
                  >
                    <button
                      type="button"
                      className="following-view__face"
                      aria-pressed={author === activeFilter}
                      aria-label={
                        author === activeFilter
                          ? `Stop showing only ${author}`
                          : `Show only ${author}`
                      }
                      onClick={() => chooseFilter(author)}
                    >
                      <AuthorPhoto author={author} />
                    </button>
                  </li>
                ))}
                <li
                  className="following-view__rail-item"
                  style={{ animationDelay: `${authors.length * 25}ms` }}
                >
                  <button
                    type="button"
                    className="following-view__face following-view__face--add"
                    onClick={() => setIsManaging(true)}
                    aria-label={`Manage the ${authors.length} ${
                      authors.length === 1 ? 'columnist' : 'columnists'
                    } you follow`}
                  >
                    +
                  </button>
                </li>
              </ul>
            </div>
          ) : (
            <FacepileCluster
              authors={authors}
              activeFilter={activeFilter}
              onExpand={() => setIsExpanded(true)}
              onClearFilter={() => setFilter(null)}
            />
          )}

          <FeedContent
            query={buildAuthorsQuery(activeFilter ? [activeFilter] : authors)}
            layout="following"
            emptyMessage="Follow columnists to fill this feed."
          />
        </>
      )}
    </section>
  );
}
